import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  Collapse,
  ListItemIcon,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  AccountBalance as AccountBalanceIcon,
  AccountTree as AccountTreeIcon,
  ArrowRight as ArrowRightIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { GLAccount, getGLAccounts } from '../firebase/glAccountServices';
// Removed dependency on @mui/lab

interface GLAccountHierarchyViewProps {
  open: boolean;
  onClose: () => void;
}

interface AccountNode {
  id: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  balanceType: 'debit' | 'credit';
  isActive: boolean;
  level: number;
  children: AccountNode[];
  currentBalance?: number;
}

const GLAccountHierarchyView: React.FC<GLAccountHierarchyViewProps> = ({
  open,
  onClose
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [accounts, setAccounts] = useState<GLAccount[]>([]);
  const [accountTree, setAccountTree] = useState<AccountNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  
  // Fetch all GL accounts
  useEffect(() => {
    if (open) {
      fetchAccounts();
    }
  }, [open]);
  
  // Build account tree when accounts change
  useEffect(() => {
    if (accounts.length > 0) {
      buildAccountTree();
    }
  }, [accounts]);
  
  // Fetch all accounts
  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const allAccounts = await getGLAccounts();
      setAccounts(allAccounts);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Build hierarchical account tree
  const buildAccountTree = () => {
    // First, get all level 1 accounts (main accounts)
    const mainAccounts = accounts.filter(account => account.level === 1);
    
    // Build tree structure
    const tree = mainAccounts.map(mainAccount => {
      // Find sub accounts for this main account
      const subAccounts = accounts.filter(
        account => account.level === 2 && account.parentAccount === mainAccount.accountNumber
      );
      
      // Build sub account nodes with their detail accounts
      const subNodes = subAccounts.map(subAccount => {
        // Find detail accounts for this sub account
        const detailAccounts = accounts.filter(
          account => account.level === 3 && account.parentAccount === subAccount.accountNumber
        );
        
        // Build detail account nodes
        const detailNodes = detailAccounts.map(detailAccount => ({
          id: detailAccount.id || detailAccount.accountNumber,
          accountNumber: detailAccount.accountNumber,
          accountName: detailAccount.accountName,
          accountType: detailAccount.accountType,
          balanceType: detailAccount.balanceType,
          isActive: detailAccount.isActive,
          level: detailAccount.level,
          currentBalance: detailAccount.currentBalance,
          children: []
        }));
        
        return {
          id: subAccount.id || subAccount.accountNumber,
          accountNumber: subAccount.accountNumber,
          accountName: subAccount.accountName,
          accountType: subAccount.accountType,
          balanceType: subAccount.balanceType,
          isActive: subAccount.isActive,
          level: subAccount.level,
          children: detailNodes
        };
      });
      
      return {
        id: mainAccount.id || mainAccount.accountNumber,
        accountNumber: mainAccount.accountNumber,
        accountName: mainAccount.accountName,
        accountType: mainAccount.accountType,
        balanceType: mainAccount.balanceType,
        isActive: mainAccount.isActive,
        level: mainAccount.level,
        children: subNodes
      };
    });
    
    // Sort by account number
    tree.sort((a, b) => parseInt(a.accountNumber) - parseInt(b.accountNumber));
    
    setAccountTree(tree);
    
    // Auto-expand main accounts
    setExpandedNodes(tree.map(node => node.id));
  };
  
  // Handle node toggle
  const handleNodeToggle = (nodeId: string) => {
    if (expandedNodes.includes(nodeId)) {
      setExpandedNodes(expandedNodes.filter(id => id !== nodeId));
    } else {
      setExpandedNodes([...expandedNodes, nodeId]);
    }
  };
  
  // Render account node
  const renderAccountNode = (node: AccountNode) => {
    const isExpanded = expandedNodes.includes(node.id);
    const hasChildren = node.children.length > 0;
    
    return (
      <React.Fragment key={node.id}>
        <ListItem 
          sx={{ 
            pl: node.level === 1 ? 2 : node.level === 2 ? 4 : 6,
            borderLeft: node.level === 1 ? '4px solid' : 'none',
            borderLeftColor: node.level === 1 ? 
              node.accountType === 'Assets' ? 'primary.main' :
              node.accountType === 'Liabilities' ? 'secondary.main' :
              node.accountType === 'Equity' ? 'success.main' :
              node.accountType === 'Revenue' ? 'info.main' : 'warning.main'
              : 'transparent',
            bgcolor: node.level === 1 ? 'rgba(0,0,0,0.04)' : 'transparent'
          }}
        >
          {hasChildren && (
            <IconButton size="small" onClick={() => handleNodeToggle(node.id)}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          
          <ListItemIcon>
            {node.level === 1 ? (
              <FolderIcon color="primary" />
            ) : node.level === 2 ? (
              <FolderOpenIcon color="secondary" />
            ) : (
              <DescriptionIcon />
            )}
          </ListItemIcon>
          
          <ListItemText 
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography 
                  variant={node.level === 1 ? 'subtitle1' : 'body1'} 
                  fontWeight={node.level === 1 ? 600 : 400}
                  sx={{ mr: 1 }}
                  component="span"
                >
                  {node.accountNumber} - {node.accountName}
                </Typography>
                {!node.isActive && (
                  <Chip size="small" label="Inactive" color="default" />
                )}
              </Box>
            }
            secondary={
              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary" component="span">
                  {node.accountType} • {node.balanceType === 'debit' ? 'Debit Balance' : 'Credit Balance'}
                </Typography>
                {node.level === 3 && node.currentBalance !== undefined && (
                  <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }} component="span">
                    Balance: RM {node.currentBalance.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </Typography>
                )}
              </Box>
            }
          />
        </ListItem>
        
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {node.children.map(childNode => renderAccountNode(childNode))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, height: '80vh' } }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <AccountTreeIcon sx={{ mr: 1 }} />
            <Typography variant="h6">GL Account Hierarchy</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography variant="body2">Loading account hierarchy...</Typography>
          </Box>
        ) : accountTree.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={3}>
            <Typography variant="body1">
              No GL accounts found. Create your first account to see the hierarchy.
            </Typography>
          </Box>
        ) : (
          <Paper 
            variant="outlined" 
            sx={{ 
              height: '100%', 
              overflow: 'auto',
              bgcolor: 'background.default'
            }}
          >
            <List component="nav" aria-label="GL account hierarchy">
              {accountTree.map(node => renderAccountNode(node))}
            </List>
          </Paper>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GLAccountHierarchyView;
